import { inject } from '@angular/core';
import { CanMatchFn, Route, Routes } from '@angular/router';
import { ModuleFlagsService } from '@shared-lib/services/module-flags.service';
import { CUSTOM_SCREEN_LOADERS } from './custom-screen-loaders.generated';
import { AuthGuard } from './shared/guards/auth.guard';

type ComponentLoader = NonNullable<Route['loadComponent']>;

const matchCustomScreen: CanMatchFn = async (route) => {
  const flags = inject(ModuleFlagsService);
  await flags.load();
  return flags.isCustom(route.path);
};

function customizableScreen(
  path: string,
  baseComponent: ComponentLoader,
  data?: Record<string, unknown>,
): Routes {
  const customComponent = CUSTOM_SCREEN_LOADERS[path];
  return [
    ...(customComponent
      ? [{ path, loadComponent: customComponent, canMatch: [matchCustomScreen], canActivate: [AuthGuard], data }]
      : []),
    { path, loadComponent: baseComponent, canActivate: [AuthGuard], data },
  ];
}

const baseHome: ComponentLoader = () =>
  import('./modules/home/home-screen.component').then(({ HomeScreenComponent }) => HomeScreenComponent);
const baseDomainCatalogs: ComponentLoader = () =>
  import('./modules/domain-catalogs/domain-catalogs.component').then(({ DomainCatalogsComponent }) => DomainCatalogsComponent);

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    loadComponent: baseHome,
    canActivate: [AuthGuard],
    data: { defaultEntry: true },
  },
  {
    path: 'access-denied',
    loadComponent: () => import('./shared/components/access-denied/access-denied.component')
      .then(({ AccessDeniedComponent }) => AccessDeniedComponent),
  },
  ...customizableScreen('home-main', baseHome, {
    configFile: 'assets/foundry-screens/home/home-main.json',
  }),
  ...customizableScreen('spriced-home', baseHome, {
    configFile: 'assets/foundry-screens/home/spriced-home.json',
  }),
  ...customizableScreen(
    'approvals',
    () => import('./modules/approval-workflow-hub/approval-workflow-hub-screen.component')
      .then(({ ApprovalWorkflowHubScreenComponent }) => ApprovalWorkflowHubScreenComponent),
    { accessKey: 'approvals', configFile: 'assets/foundry-screens/spriced/approvals.json' },
  ),
  ...customizableScreen(
    'approval-workflows',
    () => import('./modules/approval-workflow-builder/approval-workflow-builder-screen.component')
      .then(({ ApprovalWorkflowBuilderScreenComponent }) => ApprovalWorkflowBuilderScreenComponent),
    { accessKey: 'approval-workflows', configFile: 'assets/foundry-screens/spriced/approval-workflows.json' },
  ),
  ...customizableScreen(
    'price-recommendation',
    () => import('./modules/price-recommendation/price-recommendation.component')
      .then(({ PriceRecommendationScreenComponent }) => PriceRecommendationScreenComponent),
  ),
  ...customizableScreen(
    'user-access',
    () => import('./modules/user-access/user-access.component').then(({ UserAccessComponent }) => UserAccessComponent),
  ),
  {
    path: 'discount-templates',
    loadComponent: () => import('./custom/discount-templates/discount-templates.component')
      .then(({ DiscountTemplatesComponent }) => DiscountTemplatesComponent),
    canActivate: [AuthGuard],
    data: { accessKey: 'discount-templates', configFile: 'assets/foundry-screens/spriced/chetan.json' },
  },
  {
    path: 'discount-request',
    loadComponent: () => import('./custom/discount-request/discount-request.component')
      .then(({ DiscountRequestComponent }) => DiscountRequestComponent),
    canActivate: [AuthGuard],
    // Discount Request is part of the Discount Templates capability. Reusing
    // its established access key also keeps existing user permissions valid.
    data: { accessKey: 'discount-templates', configFile: 'assets/foundry-screens/spriced/discount-request.json' },
  },
  ...customizableScreen('domain-catalogs', baseDomainCatalogs, {
    configFile: 'assets/foundry-screens/spriced/item-catalogs.json',
  }),
  ...customizableScreen('customer-catalogs', baseDomainCatalogs, {
    configFile: 'assets/foundry-screens/spriced/customer-catalogs.json',
  }),
  ...customizableScreen('price-list-catalogs', baseDomainCatalogs, {
    configFile: 'assets/foundry-screens/spriced/price-list-catalogs.json',
  }),
  { path: '**', redirectTo: 'access-denied' },
];
